import { create } from '@signals/app-shell/shims/zustand';
import type { AreaBounds } from '../components/analysis/NetworkGraphMap';

export interface GraphTab {
  id: string;
  label: string;
  nodes: any[];
  links: any[];
  isOriginal: boolean;
}

type DateRange = { start: Date | null; end: Date | null };
type SetterArg<T> = T | ((prev: T) => T);
type HourSelection = { day: number; hour: number };

function defaultDateRange(): DateRange {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);
  return { start, end };
}

function defaultTabs(): GraphTab[] {
  return [{ id: 'original', label: 'Original', nodes: [], links: [], isOriginal: true }];
}

function buildQueryKey(connectionType: string, dateRange: DateRange): string | null {
  if (!dateRange.start || !dateRange.end) return null;
  return `${connectionType}|${dateRange.start.toISOString()}|${dateRange.end.toISOString()}`;
}

interface LinkAnalysisState {
  hasData: boolean;
  loadedQueryKey: string | null;
  entityTypesInitialized: boolean;

  connectionType: string;
  dateRange: DateRange;
  selectedEntityIds: string[];
  selectedEntityTypes: string[];
  selectedLocations: string[];
  selectedLocationCategories: string[];
  expandedNodeIds: string[];
  hiddenNodeIds: string[];
  connectionRange: [number, number];
  suggestedRange: [number, number];
  maxConnectionsInData: number;
  selectedDateKeys: string[] | null;
  selectedHours: HourSelection[] | null;
  selectedConnectionTypeKeys: string[] | null;
  selectedNodeTypeKeys: string[] | null;
  selectedArea: AreaBounds | null;
  isSimulationFrozen: boolean;
  showOnlyKnownDevices: boolean;
  showSentinelPanel: boolean;
  showRightPanel: boolean;
  depthLevel: number | 'all';
  graphViewMode: 'network' | 'radial';
  showSimilarPanel: boolean;
  similarPanelEntity: string | null;
  selectedCourse: string | null;
  aiHighlightedNodeIds: string[];
  tabs: GraphTab[];
  activeTab: string;
  selectionModeEnabled: boolean;

  setHasData: (v: boolean) => void;
  setLoadedQueryKey: (v: string | null) => void;
  setEntityTypesInitialized: (v: boolean) => void;
  setConnectionType: (v: string) => void;
  setDateRange: (v: SetterArg<DateRange>) => void;
  setSelectedEntityIds: (v: SetterArg<string[]>) => void;
  setSelectedEntityTypes: (v: SetterArg<string[]>) => void;
  setSelectedLocations: (v: SetterArg<string[]>) => void;
  setSelectedLocationCategories: (v: SetterArg<string[]>) => void;
  setExpandedNodeIds: (v: SetterArg<string[]>) => void;
  setHiddenNodeIds: (v: SetterArg<string[]>) => void;
  setConnectionRange: (v: [number, number]) => void;
  setSuggestedRange: (v: [number, number]) => void;
  setMaxConnectionsInData: (v: number) => void;
  setSelectedDateKeys: (v: string[] | null) => void;
  setSelectedHours: (v: HourSelection[] | null) => void;
  setSelectedConnectionTypeKeys: (v: string[] | null) => void;
  setSelectedNodeTypeKeys: (v: string[] | null) => void;
  setSelectedArea: (v: AreaBounds | null) => void;
  setIsSimulationFrozen: (v: boolean) => void;
  setShowOnlyKnownDevices: (v: boolean) => void;
  setShowSentinelPanel: (v: boolean) => void;
  setShowRightPanel: (v: boolean) => void;
  setDepthLevel: (v: number | 'all') => void;
  setGraphViewMode: (v: 'network' | 'radial') => void;
  setShowSimilarPanel: (v: boolean) => void;
  setSimilarPanelEntity: (v: string | null) => void;
  setSelectedCourse: (v: string | null) => void;
  setAiHighlightedNodeIds: (v: SetterArg<string[]>) => void;
  setTabs: (v: SetterArg<GraphTab[]>) => void;
  setActiveTab: (v: string) => void;
  setSelectionModeEnabled: (v: boolean) => void;
  markDataLoaded: (connectionType: string, dateRange: DateRange) => void;
  resetSession: () => void;
}

const initialState = {
  hasData: false,
  loadedQueryKey: null as string | null,
  entityTypesInitialized: false,
  connectionType: 'digital',
  dateRange: defaultDateRange(),
  selectedEntityIds: [] as string[],
  selectedEntityTypes: [] as string[],
  selectedLocations: [] as string[],
  selectedLocationCategories: [] as string[],
  expandedNodeIds: [] as string[],
  hiddenNodeIds: [] as string[],
  connectionRange: [1, Infinity] as [number, number],
  suggestedRange: [1, 50] as [number, number],
  maxConnectionsInData: 50,
  selectedDateKeys: null as string[] | null,
  selectedHours: null as HourSelection[] | null,
  selectedConnectionTypeKeys: null as string[] | null,
  selectedNodeTypeKeys: null as string[] | null,
  selectedArea: null as AreaBounds | null,
  isSimulationFrozen: false,
  showOnlyKnownDevices: false,
  showSentinelPanel: true,
  showRightPanel: true,
  depthLevel: 'all' as number | 'all',
  graphViewMode: 'network' as 'network' | 'radial',
  showSimilarPanel: false,
  similarPanelEntity: null as string | null,
  selectedCourse: null as string | null,
  aiHighlightedNodeIds: [] as string[],
  tabs: defaultTabs(),
  activeTab: 'original',
  selectionModeEnabled: false,
};

export const useLinkAnalysisStore = create<LinkAnalysisState>((set) => ({
  ...initialState,

  setHasData: (v) => set({ hasData: v }),
  setLoadedQueryKey: (v) => set({ loadedQueryKey: v }),
  setEntityTypesInitialized: (v) => set({ entityTypesInitialized: v }),
  setConnectionType: (v) => set({ connectionType: v }),
  setDateRange: (v) => set((s) => ({ dateRange: typeof v === 'function' ? v(s.dateRange) : v })),
  setSelectedEntityIds: (v) => set((s) => ({ selectedEntityIds: typeof v === 'function' ? v(s.selectedEntityIds) : v })),
  setSelectedEntityTypes: (v) => set((s) => ({ selectedEntityTypes: typeof v === 'function' ? v(s.selectedEntityTypes) : v })),
  setSelectedLocations: (v) => set((s) => ({ selectedLocations: typeof v === 'function' ? v(s.selectedLocations) : v })),
  setSelectedLocationCategories: (v) => set((s) => ({ selectedLocationCategories: typeof v === 'function' ? v(s.selectedLocationCategories) : v })),
  setExpandedNodeIds: (v) => set((s) => ({ expandedNodeIds: typeof v === 'function' ? v(s.expandedNodeIds) : v })),
  setHiddenNodeIds: (v) => set((s) => ({ hiddenNodeIds: typeof v === 'function' ? v(s.hiddenNodeIds) : v })),
  setConnectionRange: (v) => set({ connectionRange: v }),
  setSuggestedRange: (v) => set({ suggestedRange: v }),
  setMaxConnectionsInData: (v) => set({ maxConnectionsInData: v }),
  setSelectedDateKeys: (v) => set({ selectedDateKeys: v }),
  setSelectedHours: (v) => set({ selectedHours: v }),
  setSelectedConnectionTypeKeys: (v) => set({ selectedConnectionTypeKeys: v }),
  setSelectedNodeTypeKeys: (v) => set({ selectedNodeTypeKeys: v }),
  setSelectedArea: (v) => set({ selectedArea: v }),
  setIsSimulationFrozen: (v) => set({ isSimulationFrozen: v }),
  setShowOnlyKnownDevices: (v) => set({ showOnlyKnownDevices: v }),
  setShowSentinelPanel: (v) => set({ showSentinelPanel: v }),
  setShowRightPanel: (v) => set({ showRightPanel: v }),
  setDepthLevel: (v) => set({ depthLevel: v }),
  setGraphViewMode: (v) => set({ graphViewMode: v }),
  setShowSimilarPanel: (v) => set({ showSimilarPanel: v }),
  setSimilarPanelEntity: (v) => set({ similarPanelEntity: v }),
  setSelectedCourse: (v) => set({ selectedCourse: v }),
  setAiHighlightedNodeIds: (v) => set((s) => ({ aiHighlightedNodeIds: typeof v === 'function' ? v(s.aiHighlightedNodeIds) : v })),
  setTabs: (v) => set((s) => ({ tabs: typeof v === 'function' ? v(s.tabs) : v })),
  setActiveTab: (v) => set({ activeTab: v }),
  setSelectionModeEnabled: (v) => set({ selectionModeEnabled: v }),

  markDataLoaded: (connectionType, dateRange) => {
    const key = buildQueryKey(connectionType, dateRange);
    set({
      hasData: true,
      loadedQueryKey: key,
    });
  },

  resetSession: () => set({
    ...initialState,
    dateRange: defaultDateRange(),
    tabs: defaultTabs(),
  }),
}));

export { buildQueryKey };
